import { useRef, useState, ChangeEventHandler } from "react";
import dayjs from "dayjs";
import I18n from "common/i18n";
import { usePlaySound } from "common/utils";
import { RadioButton, Select } from "common/elements";
import { AppUser } from "../types";

type ProfileBasicTabProps = {
  user: AppUser;
  icon: string;
  name: string;
  gender: string;
  selectedYear: number | null;
  selectedMonth: number | null;
  selectedDay: number | null;
  onNameChange: ChangeEventHandler<HTMLInputElement>;
  onGenderChange: (gender: string) => void;
  onYearChange: (year: number) => void;
  onMonthChange: (month: number) => void;
  onDayChange: (day: number) => void;
  onOpenIconModal: () => void;
};

export const ProfileBasicTab = ({
  user: _user,
  icon,
  name,
  gender,
  selectedYear,
  selectedMonth,
  selectedDay,
  onNameChange,
  onGenderChange,
  onYearChange,
  onMonthChange,
  onDayChange,
  onOpenIconModal,
}: ProfileBasicTabProps) => {
  const play = usePlaySound();
  const [yearOpen, setYearOpen] = useState(false);
  const [monthOpen, setMonthOpen] = useState(false);
  const [dayOpen, setDayOpen] = useState(false);
  const yearRef = useRef<HTMLButtonElement>(null);
  const monthRef = useRef<HTMLButtonElement>(null);
  const dayRef = useRef<HTMLButtonElement>(null);

  const today = dayjs();
  const currentYear = today.year();
  const yearSuffixKey = "MSG_BIRTHDAY_YEAR_SUFFIX";
  const monthSuffixKey = "MSG_BIRTHDAY_MONTH_SUFFIX";
  const daySuffixKey = "MSG_BIRTHDAY_DAY_SUFFIX";

  const yearOptions = Array.from({ length: 100 }, (_, i) => {
    const year = currentYear - i;
    return { value: year, label: `${year}${I18n.t(yearSuffixKey)}` };
  });
  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}${I18n.t(monthSuffixKey)}`,
  }));
  const daysInMonth =
    selectedYear && selectedMonth ? dayjs(`${selectedYear}-${selectedMonth}-01`).daysInMonth() : 31;
  const dayOptions = Array.from({ length: daysInMonth }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}${I18n.t(daySuffixKey)}`,
  }));

  const handleGenderChange = (genderValue: string) => () => {
    play();
    onGenderChange(genderValue);
  };

  return (
    <div className="flex-row-el flex-center gap-12">
      <div className="flex-col-el flex-center gap-6">
        <div
          id="user-page-profile-icon"
          className="flex-col-center cursor-pointer bg-white rounded-full w-[180px] h-[180px]"
          onClick={onOpenIconModal}
          style={{
            boxShadow: "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
          }}
        >
          <div className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden">
            <img
              alt="プレイヤープロフィール画像"
              src={icon}
              className="w-full h-full object-contain"
            />
          </div>
        </div>
        <div className="flex-row-el flex-center upsilon">
          <button className="btn btn-primary" onClick={onOpenIconModal}>
            <div className="flex-row-center gap-1 mb-[3px]">
              <span className="material-symbols-outlined !text-3xl text-white">edit_note</span>
              <p className="text-body text-white text-font-size-body2">
                {I18n.t("MSG_USER_PROFILE_ICON_BTN")}
              </p>
            </div>
          </button>
        </div>
      </div>

      <div className="flex-col-el relative items-start justify-center gap-8 w-[680px]">
        <div className="flex-row-el items-center justify-start relative w-full">
          <div className="flex-col-el w-[160px] relative shrink-0">
            <label className="text-body text-textcolor-black text-[20px]">
              {I18n.t("MSG_USER_PROFILE_FIELD_NAME")}
            </label>
          </div>
          <div className="flex-col-el flex-center relative w-[502px]">
            <input
              required
              type="text"
              className="peer input text-body text-center text-textcolor-black text-[28px]"
              placeholder={I18n.t("MSG_USER_PROFILE_FIELD_NAME_PLACEHOLDER")}
              pattern={".{1,10}"}
              autoFocus={false}
              value={name}
              onChange={onNameChange}
            />
            <label className="text-body text-left text-[14px] h-5 text-danger invisible peer-invalid:visible">
              {I18n.t("MSG_BLOCKLY_INPUT_LENGTH_LIMIT", { limit: 10 })}
            </label>
          </div>
        </div>

        <div className="flex-row-el items-center justify-start relative w-full">
          <div className="flex-col-el w-[160px] relative shrink-0">
            <label className="text-body text-textcolor-black text-[20px]">
              {I18n.t("MSG_USER_PROFILE_FIELD_GENDER")}
            </label>
          </div>
          <div className="flex-row-el items-center justify-between w-[502px]">
            <div className="flex-row-center">
              <RadioButton
                size={34}
                value={gender === "male"}
                label={I18n.t("MSG_USER_PROFILE_FIELD_GENDER_MALE")}
                fontSize={20}
                onChange={handleGenderChange("male")}
              />
            </div>
            <div className="flex-row-center">
              <RadioButton
                size={34}
                value={gender === "female"}
                label={I18n.t("MSG_USER_PROFILE_FIELD_GENDER_FEMALE")}
                fontSize={20}
                onChange={handleGenderChange("female")}
              />
            </div>
            <div className="flex-row-center">
              <RadioButton
                size={34}
                value={gender === "none"}
                label={I18n.t("MSG_USER_PROFILE_FIELD_GENDER_NONE")}
                fontSize={20}
                onChange={handleGenderChange("none")}
              />
            </div>
          </div>
        </div>

        <div className="flex-row-el items-center justify-start relative w-full">
          <div className="flex-col-el w-[160px] relative shrink-0">
            <label className="text-body text-textcolor-black text-[20px]">
              {I18n.t("MSG_USER_PROFILE_FIELD_BIRTHDAY")}
            </label>
          </div>
          <div className="flex-row-el flex-center gap-4">
            <button
              ref={yearRef}
              onClick={() => {
                play();
                setYearOpen(true);
              }}
              className="flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[20px] cursor-pointer select-none active:scale-95 bg-white border-4 border-input-focus text-textcolor-black w-[170px]"
            >
              <span>
                {selectedYear ? `${selectedYear}${I18n.t(yearSuffixKey)}` : I18n.t(yearSuffixKey)}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={yearRef}
              open={yearOpen}
              onClose={() => setYearOpen(false)}
              value={selectedYear}
              onChange={onYearChange}
              options={yearOptions}
            />

            <button
              ref={monthRef}
              onClick={() => {
                play();
                setMonthOpen(true);
              }}
              className="flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[20px] cursor-pointer select-none active:scale-95 bg-white border-4 border-input-focus text-textcolor-black w-[150px]"
            >
              <span>
                {selectedMonth
                  ? `${selectedMonth}${I18n.t(monthSuffixKey)}`
                  : I18n.t(monthSuffixKey)}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={monthRef}
              open={monthOpen}
              onClose={() => setMonthOpen(false)}
              value={selectedMonth}
              onChange={onMonthChange}
              options={monthOptions}
            />

            <button
              ref={dayRef}
              onClick={() => {
                play();
                setDayOpen(true);
              }}
              className="flex items-center justify-between gap-2 py-3 px-4 rounded-[8px] text-[20px] cursor-pointer select-none active:scale-95 bg-white border-4 border-input-focus text-textcolor-black w-[150px]"
            >
              <span>
                {selectedDay ? `${selectedDay}${I18n.t(daySuffixKey)}` : I18n.t(daySuffixKey)}
              </span>
              <span className="material-symbols-outlined !text-[24px]">expand_more</span>
            </button>
            <Select
              anchorRef={dayRef}
              open={dayOpen}
              onClose={() => setDayOpen(false)}
              value={selectedDay}
              onChange={onDayChange}
              options={dayOptions}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
